/**
 * Notification URL Routing Utility
 *
 * Maps notification types and related entities to their corresponding URLs.
 * Provides fallback routing when action_url is not set on a notification.
 */

import type { Notification, NotificationType } from "../types/notifications";

/**
 * Default URL routes by notification type
 * These are used when related_entity_id is not available
 */
const DEFAULT_ROUTES: Partial<Record<NotificationType, string>> = {
    // Account & Onboarding
    ACCOUNT_CREATED: "/dashboard",
    ACCOUNT_APPROVED: "/dashboard",
    ACCOUNT_REJECTED: "/dashboard",
    ONBOARDING_COMPLETE: "/dashboard",
    ONBOARDING_REMINDER: "/dashboard",
    ACCOUNT_SUSPENDED: "/dashboard",
    ACCOUNT_VERIFICATION: "/dashboard",

    // Documents
    DOCUMENT_VERIFICATION_REQUIRED: "/documents",
    DOCUMENT_VERIFICATION_ASSIGNED: "/documents",
    DOCUMENT_APPROVED: "/documents",
    DOCUMENT_REJECTED: "/documents",
    DOCUMENT_UPLOADED: "/documents",
    DOCUMENT_EXPIRY_WARNING: "/documents",
    DOCUMENT_EXPIRY_CRITICAL: "/documents",
    VETTING_STARTED: "/documents",
    VETTING_COMPLETED: "/documents",

    // Jobs
    JOB_CREATED: "/dashboard/jobs",
    JOB_UPDATED: "/dashboard/jobs",
    JOB_STATUS_CHANGED: "/dashboard/jobs",
    JOB_APPROVED: "/dashboard/jobs",
    JOB_ASSIGNED: "/dashboard/jobs",
    JOB_COMPLETED: "/dashboard/jobs",
    JOB_CANCELLED: "/dashboard/jobs",
    JOB_NEEDS_INFO: "/dashboard/jobs",
    JOB_AVAILABLE: "/dashboard/jobs",

    // Quotes
    QUOTE_RECEIVED: "/dashboard/quotes",
    QUOTE_APPROVED: "/dashboard/quotes",
    QUOTE_REQUESTED: "/dashboard/quotes",
    QUOTE_SUBMITTED: "/dashboard/quotes",
    QUOTE_REJECTED: "/dashboard/quotes",
    QUOTE_DECLINED: "/dashboard/quotes",
    QUOTE_SUPERSEDED: "/dashboard/quotes",

    // Payments
    PAYMENT_DUE: "/dashboard/payments",
    PAYMENT_RECEIVED: "/dashboard/payments",
    PAYMENT_FAILED: "/dashboard/payments",
    PAYMENT_METHOD_EXPIRING: "/settings/billing",
    REFUND_ISSUED: "/dashboard/payments",
    PAYMENT_RELEASED: "/dashboard/payments",
    PAYMENT_PROCESSED: "/dashboard/payments",

    // Messages
    MESSAGE_RECEIVED: "/dashboard/messages",

    // Compliance
    COMPLIANCE_ALERT: "/documents",
    COMPLIANCE_REMINDER: "/documents",

    // Auth & System
    PASSWORD_RESET: "/dashboard",
    INVITATION: "/dashboard",
    EMAIL_CHANGE: "/settings/profile",
    MAGIC_LINK: "/dashboard",
    SYSTEM_MESSAGE: "/dashboard",
};

/**
 * Entity type to URL path mapping
 */
const ENTITY_ROUTES: Record<string, string> = {
    job: "/dashboard/jobs",
    jobs: "/dashboard/jobs",
    document: "/documents",
    documents: "/documents",
    quote: "/dashboard/quotes",
    quotes: "/dashboard/quotes",
    conversation: "/dashboard/messages",
    message: "/dashboard/messages",
    messages: "/dashboard/messages",
    payment: "/dashboard/payments",
    payments: "/dashboard/payments",
    customer: "/dashboard/customers",
    customers: "/dashboard/customers",
    trade_company: "/dashboard/trades",
    trade_companies: "/dashboard/trades",
    user: "/settings/team",
    profile: "/settings/profile",
    site_issue: "/admin/issues",
    site_issues: "/admin/issues",
};

const TRADE_CONNECTOR_HOSTS = new Set([
    "tradeconnector.com.au",
    "www.tradeconnector.com.au",
    "app.tradeconnector.com.au",
]);

function getDetectedAppHost(): string | null {
    if (typeof window !== "undefined" && window.location?.hostname) {
        return window.location.hostname.toLowerCase();
    }
    const env = typeof process !== "undefined" && process.env ? process.env : undefined;
    const envUrl = env?.VITE_PUBLIC_APP_URL || env?.PUBLIC_APP_URL || env?.SITE_URL;
    if (envUrl && typeof envUrl === "string") {
        try {
            return new URL(envUrl).hostname.toLowerCase();
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * Check if a hostname belongs to Trade Connector
 */
export function isTradeConnectorHost(hostname: string, configuredAppHost?: string): boolean {
    const lower = hostname.toLowerCase();
    if (TRADE_CONNECTOR_HOSTS.has(lower) || lower.endsWith(".tradeconnector.com.au")) {
        return true;
    }
    if (configuredAppHost !== undefined) {
        // Accept a full URL (e.g. VITE_PUBLIC_APP_URL) as well as a bare hostname.
        let expected = configuredAppHost.toLowerCase();
        if (expected.includes("://")) {
            try {
                expected = new URL(expected).hostname.toLowerCase();
            } catch {
                // keep the literal value
            }
        }
        return lower === expected;
    }
    const detected = getDetectedAppHost();
    if (detected && lower === detected) {
        return true;
    }
    return false;
}

/**
 * Normalize an action URL:
 * - If it is an absolute URL whose host is a Trade Connector host, convert it to path + query + hash.
 * - Otherwise (relative path or external absolute URL), return as-is.
 */
export function normalizeActionUrl(url: string, configuredAppHost?: string): string {
    if (!url) return url;
    try {
        const parsed = new URL(url);
        if (isTradeConnectorHost(parsed.hostname, configuredAppHost)) {
            const rel = parsed.pathname + parsed.search + parsed.hash;
            return rel.startsWith("/") ? rel : `/${rel}`;
        }
        return url;
    } catch {
        // Not a valid absolute URL, assume relative
        return url;
    }
}

/**
 * Check if a URL points to an external site
 */
export function isExternalUrl(url: string, configuredAppHost?: string): boolean {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return !isTradeConnectorHost(parsed.hostname, configuredAppHost);
    } catch {
        return false;
    }
}

/**
 * Get the navigation URL for a notification
 *
 * Priority:
 * 1. Use action_url if present (normalized if pointing to Trade Connector)
 * 2. Use related_entity_type + related_entity_id to build URL
 * 3. Use notification_type default route
 * 4. Fall back to /dashboard
 */
export function getNotificationUrl(notification: Notification, configuredAppHost?: string): string {
    // Priority 1: Use action_url if set
    if (notification.action_url) {
        return normalizeActionUrl(notification.action_url, configuredAppHost);
    }

    // Priority 2: Build URL from related entity
    if (notification.related_entity_type && notification.related_entity_id) {
        const entityType = notification.related_entity_type.toLowerCase();
        const basePath = ENTITY_ROUTES[entityType];

        if (basePath) {
            // Special handling for different route styles
            // Messages use path params: /messages/:id
            if (["conversation", "message", "messages"].includes(entityType)) {
                return `${basePath}/${notification.related_entity_id}`;
            }

            // Jobs use path params: /jobs/:id
            if (["job", "jobs"].includes(entityType)) {
                return `${basePath}/${notification.related_entity_id}`;
            }

            // Quotes use query params: /quotes?quoteId=:id
            if (["quote", "quotes"].includes(entityType)) {
                return `${basePath}?quoteId=${notification.related_entity_id}`;
            }

            // Site issues use query params: /admin/issues?id=:id
            if (["site_issue", "site_issues"].includes(entityType)) {
                return `${basePath}?id=${notification.related_entity_id}`;
            }

            // Default: just return base path
            return basePath;
        }
    }

    // Priority 3: Use notification type default route
    const defaultRoute = DEFAULT_ROUTES[notification.notification_type];
    if (defaultRoute) {
        // If we have an entity ID but no matching type, append it to the default route
        if (
            notification.related_entity_id &&
            canAppendEntityId(notification.notification_type)
        ) {
            // Jobs use path params; quotes still use query params
            if (defaultRoute.includes("/jobs")) {
                return `${defaultRoute}/${notification.related_entity_id}`;
            }
            if (defaultRoute.includes("/quotes")) {
                return `${defaultRoute}?quoteId=${notification.related_entity_id}`;
            }
            // For others (like messages), append as path parameter
            return `${defaultRoute}/${notification.related_entity_id}`;
        }
        return defaultRoute;
    }

    // Priority 4: Fall back to dashboard
    return "/dashboard";
}

/**
 * Check if we should append entity ID to the default route
 * Some notification types naturally point to a specific entity
 */
function canAppendEntityId(type: NotificationType): boolean {
    const typesWithEntity: NotificationType[] = [
        "JOB_CREATED",
        "JOB_UPDATED",
        "JOB_STATUS_CHANGED",
        "JOB_APPROVED",
        "JOB_ASSIGNED",
        "JOB_COMPLETED",
        "JOB_CANCELLED",
        "JOB_NEEDS_INFO",
        "JOB_AVAILABLE",
        "QUOTE_RECEIVED",
        "QUOTE_APPROVED",
        "QUOTE_REQUESTED",
        "QUOTE_SUBMITTED",
        "QUOTE_REJECTED",
        "QUOTE_DECLINED",
        "QUOTE_SUPERSEDED",
        "MESSAGE_RECEIVED",
    ];

    return typesWithEntity.includes(type);
}

/**
 * Check if a notification is clickable (has a destination)
 */
export function isNotificationClickable(notification: Notification): boolean {
    return Boolean(
        notification.action_url ||
            (notification.related_entity_type &&
                notification.related_entity_id) ||
            DEFAULT_ROUTES[notification.notification_type],
    );
}

/**
 * Get a human-readable description of where clicking will navigate
 */
export function getNotificationDestinationLabel(
    notification: Notification,
): string | null {
    const url = getNotificationUrl(notification);

    if (url.includes("/jobs/") || url.includes("?jobId=")) return "View Job";
    if (url.includes("/jobs")) return "View Jobs";
    if (url.includes("/documents/")) return "View Document";
    if (url.includes("/documents")) return "View Documents";
    if (url.includes("/quotes/") || url.includes("?quoteId=")) {
        return "View Quote";
    }
    if (url.includes("/quotes")) return "View Quotes";
    if (url.includes("/messages/")) return "View Message";
    if (url.includes("/messages")) return "View Messages";
    if (url.includes("/payments")) return "View Payments";
    if (url.includes("/settings")) return "View Settings";
    if (url === "/dashboard") return "Go to Dashboard";

    return "View Details";
}
